import {forwardRef, type PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';
import {CircularLoader} from '../circular-loader';
import {cn} from '../lib/cn';

type TableLoaderProps = {
  label?: string;
  className?: string;
  hideBorder?: boolean;
  variant?: 'primary' | 'secondary';
};

type TableLoaderRootProps = PropsWithChildren & {
  columns: number;
};

const LoaderComponent = forwardRef<HTMLDivElement, Readonly<TableLoaderProps>>(
  ({label, className, hideBorder, variant = 'primary'}, ref) => {
    const {t} = useTranslation();

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-1 cursor-default items-center justify-center pb-12 pt-8',
          !hideBorder && 'border-t border-[var(--table-loader-border)]',
          className,
        )}
      >
        <CircularLoader
          size={variant === 'primary' ? 'lg' : 'md'}
          label={label || t('loading')}
          className={cn(
            'flex-row gap-3 space-y-0 [--circular-loader-color:var(--table-loader-color)]',
            '[&_div]:text-sm [&_div]:font-bold [&_div]:uppercase [&_div]:text-[var(--table-loader-label-color)] [&_div]:opacity-50',
          )}
        />
      </div>
    );
  },
);

LoaderComponent.displayName = 'TableLoader.Loader';

function Root({children, columns}: TableLoaderRootProps) {
  return (
    <tr className="cursor-pointer hover:!bg-[var(--table-loader-row-hover-bg)]">
      <td colSpan={columns} className="cursor-default pl-0">
        {children}
      </td>
    </tr>
  );
}

const TableLoader = {
  Loader: LoaderComponent,
  Root,
};

export {TableLoader};
export type {TableLoaderProps, TableLoaderRootProps};
